import { Location, NgTemplateOutlet } from '@angular/common'
import { Component, computed, inject, OnInit, signal } from '@angular/core'
import { RouterLink } from '@angular/router'
import { PublicRequestService } from '@ftr/api-core'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  AlertComponent,
  AlertTypes,
  AnchorComponent,
  ApiResult,
  ButtonComponent,
  EmptyStateComponent,
  IconButtonComponent,
  Illustration,
  mapData,
  PageLayoutComponent,
  PageStyle,
  selectWith,
  TypedContextDirective,
} from '@ftr/foundation'
import { AppPaths, RequestPaths } from '@ftr/routing-paths'
import { ZoneId } from '@js-joda/core'
import { Store } from '@ngxs/store'
import { BehaviorSubject, switchMap } from 'rxjs'
import { CartState, CartTime, RemoveCartItems, UpdateCartItem } from '../../store'
import { RequestTypeUrlValue } from '../../types'
import { RequestCartGroupMetadataComponent } from './cart-group-metadata'
import { CartItemFormComponent } from './cart-item-form'
import { CartTimesDisplayComponent } from './cart-times-display'

/*
 * Cart is currently limited to recording orders.
 * A recording request type URL is required for correct user navigation.
 */
export const CART_ITEM_REQUEST_TYPE = RequestTypeUrlValue.Recordings

interface CartViewModel {
  configuration: PublicConfiguration
}

@Component({
  selector: 'ftr-request-cart',
  templateUrl: './request-cart.component.html',
  styleUrl: './request-cart.component.css',
  imports: [
    AnchorComponent,
    CartItemFormComponent,
    CartTimesDisplayComponent,
    EmptyStateComponent,
    IconButtonComponent,
    PageLayoutComponent,
    RequestCartGroupMetadataComponent,
    RouterLink,
    TypedContextDirective,
    AlertComponent,
    ButtonComponent,
    NgTemplateOutlet,
  ],
})
export class RequestCartComponent implements OnInit {
  private readonly publicRequestService = inject(PublicRequestService)
  private readonly store = inject(Store)
  protected readonly location = inject(Location)

  protected viewModel$!: ApiResult<CartViewModel | undefined>
  protected readonly refresh = new BehaviorSubject<void>(undefined)
  protected readonly pageStyle = PageStyle.Fluid
  protected readonly emptyIllustration = Illustration.Cart

  protected readonly itemIdBeingEdited = signal<Uuid | null>(null)

  protected readonly cartGroups = selectWith(() => CartState.groupedCartItems)
  protected readonly courtSystem = selectWith(() => CartState.cartCourtSystem)
  protected readonly cartIsPopulated = computed<boolean>(() => this.cartGroups().length > 0)
  protected readonly checkoutPath = computed<string[] | undefined>(() => {
    const courtSystem = this.courtSystem()
    if (!courtSystem) {
      return undefined
    }

    return [
      '/',
      AppPaths.Request,
      CART_ITEM_REQUEST_TYPE,
      RequestPaths.CourtSystem,
      courtSystem.id,
      RequestPaths.Checkout,
    ]
  })
  protected readonly recordingsRequestPath = computed(() => [
    '/',
    AppPaths.Request,
    RequestPaths.Recordings,
    RequestPaths.ChooseCourt,
  ])
  protected readonly transcriptRequestPath = computed(() => [
    '/',
    AppPaths.Request,
    RequestPaths.Transcripts,
    RequestPaths.ChooseCourt,
  ])
  protected removedCartItems = false

  protected readonly alertTypes = AlertTypes

  ngOnInit(): void {
    const courtSystemId = this.courtSystem()?.id

    if (!courtSystemId || !this.cartIsPopulated()) {
      this.viewModel$ = ApiResult.success(undefined)
      return
    }

    this.viewModel$ = this.refresh.pipe(
      switchMap(() =>
        ApiResult.combine({
          configuration: this.publicRequestService.getConfiguration(courtSystemId),
        }).pipe(mapData(({ configuration }) => ({ configuration }))),
      ),
    )

    this.removeStaleCartItems()
  }

  protected openEditItemForm(itemId: Uuid): void {
    this.itemIdBeingEdited.set(itemId)
  }

  protected closeEditItemForm(): void {
    this.itemIdBeingEdited.set(null)
  }

  protected removeItems(itemIds: string[]): void {
    this.store.dispatch(new RemoveCartItems(itemIds))
  }

  protected onItemTimeframesSaved(itemId: Uuid, requestedTimes: CartTime[]): void {
    this.store.dispatch(new UpdateCartItem(itemId, { requestedTimes }))
    this.closeEditItemForm()
  }

  // TODO - Fix public config API response to either de/serialize ZoneId properly or just send the timeZoneId string
  protected getCourtTimeZoneId(timeZoneId: unknown): ZoneId {
    try {
      // API response currently deserializing as this weird shape
      if (timeZoneId && typeof timeZoneId === 'object' && '_id' in timeZoneId && typeof timeZoneId._id === 'string') {
        return ZoneId.of(timeZoneId._id)
      }

      if (timeZoneId instanceof ZoneId) {
        return timeZoneId
      }

      if (typeof timeZoneId === 'string') {
        return ZoneId.of(timeZoneId)
      }

      throw new Error(`Invalid timeZoneId format: ${timeZoneId}`)
    } catch (error) {
      console.error('Failed to parse timeZoneId, falling back to UTC:', error)
      return ZoneId.UTC
    }
  }

  protected removeStaleCartItems(): void {
    // TODO DI-3118: Get sessions via API for cart items (use session ID) and remove any stale items.
    // If items removed, set removedCartItems = true
  }
}
